import React, { useEffect, useRef, useState } from 'react';
import { FaArrowRight, FaBrain, FaCompass, FaDownload, FaExternalLinkAlt, FaMobileAlt, FaPlay, FaQuoteRight, FaShoppingBag, FaTimes, FaWallet } from 'react-icons/fa';
import { SiAgora, SiDart, SiFirebase, SiFlutter, SiMongodb } from 'react-icons/si';

const projects = [
  {
    name: 'VitalSync AI',
    category: 'Health & Fitness',
    dates: 'June 2026 - Present',
    context: 'Personal platform',
    description: 'An AI-powered health and fitness platform for everyday wellbeing.',
    overview: 'VitalSync AI brings nutrition, workouts, daily activity, water intake, and health progress together in one personalized platform.',
    features: ['Nutrition tracking', 'Workout tracking', 'Daily activity and water intake', 'Personalized health-progress insights'],
    technologies: [
      { name: 'AI-powered platform', icon: FaBrain },
      { name: 'Mobile app', icon: FaMobileAlt },
    ],
    links: [
      { label: 'Open', url: 'https://vitalsyncai.web.app', icon: FaExternalLinkAlt, external: true },
    ],
    visualIcon: FaBrain,
    accent: 'from-rose-300/25 via-violet-400/10 to-cyan-300/20',
  },
  {
    name: 'FlashCard Quiz App',
    category: 'Education',
    dates: 'September 2026 - October 2026',
    context: 'CodeAlpha',
    description: 'Interactive digital flashcards for focused study and revision.',
    overview: 'A Flutter study app that helps students organize, review, and memorize important concepts with simple, effective digital flashcards.',
    features: ['Create and organize study cards', 'Review concepts interactively', 'Focused memorization workflow'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'Dart', icon: SiDart },
    ],
    links: [],
    visualIcon: FaMobileAlt,
    accent: 'from-amber-300/25 via-orange-400/10 to-violet-300/20',
  },
  {
    name: 'Random Quote Generator',
    category: 'Lifestyle',
    dates: 'September 2026 - October 2026',
    context: 'CodeAlpha',
    description: 'A Flutter quote app with a fresh, inspiring collection on demand.',
    overview: 'A Flutter application with API integration that delivers fresh inspiring and thought-provoking quotes through a simple, engaging experience.',
    features: ['Fresh quotes on demand', 'API integration', 'Simple, engaging interface'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'Dart', icon: SiDart },
    ],
    links: [],
    visualIcon: FaQuoteRight,
    accent: 'from-fuchsia-300/25 via-indigo-400/10 to-cyan-300/20',
  },
  {
    name: 'Meetchat',
    category: 'Communication',
    dates: 'March 2025 - June 2025',
    context: 'Personal project',
    description: 'A real-time chat and audio/video calling application.',
    overview: 'Meetchat brings messaging and audio/video calling together in a Flutter application connected to Firebase and Agora.',
    features: ['Real-time chatting', 'Audio calling', 'Video calling'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'Firebase', icon: SiFirebase },
      { name: 'Agora', icon: SiAgora },
    ],
    links: [
      { label: 'Download APK', url: 'https://drive.google.com/uc?export=download&id=1HRLecJgn7-RqPFwmBGxMuOLYwfUpMYBM', icon: FaDownload, external: true },
    ],
    visualIcon: FaMobileAlt,
    accent: 'from-indigo-400/30 via-violet-400/10 to-cyan-300/20',
  },
  {
    name: 'E-Shop',
    category: 'E-Commerce',
    dates: 'May 2024 - August 2024',
    context: 'Personal project',
    description: 'An e-commerce shopping app with an accompanying admin panel.',
    overview: 'E-Shop explores a Flutter commerce experience using GetX and MongoDB across the shopping app and its admin panel.',
    features: ['Shopping experience', 'Admin panel'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'GetX', icon: FaMobileAlt },
      { name: 'MongoDB', icon: SiMongodb },
    ],
    links: [
      { label: 'Watch UI Demo', url: 'https://youtube.com/shorts/g6_wgB9dw3Q?feature=share', icon: FaPlay, external: true },
    ],
    availability: 'APK unavailable because the backend is no longer running.',
    visualIcon: FaShoppingBag,
    accent: 'from-violet-400/25 via-fuchsia-400/10 to-indigo-300/15',
  },
  {
    name: 'Islamic App',
    category: 'Utility',
    dates: 'June 2025 - August 2025',
    context: 'Personal project',
    description: 'A prayer utility with prayer time API and Qibla direction features.',
    overview: 'The Islamic App focuses on practical prayer utilities, including prayer time API integration and Qibla direction.',
    features: ['Prayer time API integration', 'Qibla direction'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'Dart', icon: SiDart },
    ],
    links: [
      { label: 'Download Page', url: 'https://com-example-prayer-times.en.uptodown.com/android', icon: FaExternalLinkAlt, external: true },
    ],
    visualIcon: FaCompass,
    accent: 'from-cyan-300/25 via-teal-400/10 to-indigo-400/15',
  },
  {
    name: 'Expense Tracker',
    category: 'Productivity',
    dates: 'February 2026 - March 2026',
    context: 'Personal project',
    description: 'A Flutter app for tracking expenses with local storage.',
    overview: 'Expense Tracker keeps everyday expense records available locally in a focused Flutter application.',
    features: ['Expense tracking', 'Local storage'],
    technologies: [
      { name: 'Flutter', icon: SiFlutter },
      { name: 'Dart', icon: SiDart },
    ],
    links: [
      { label: 'Download APK', url: 'https://drive.google.com/uc?export=download&id=1G0kvx3E7NkFQNnTDcr1KYUmjJj1HLIJ6', icon: FaDownload, external: true },
    ],
    visualIcon: FaWallet,
    accent: 'from-emerald-300/20 via-cyan-400/10 to-indigo-400/15',
  },
];

function ProjectVisual({ project, featured = false }) {
  const Icon = project.visualIcon;
  return (
    <div className={`group/visual relative min-h-60 overflow-hidden rounded-2xl border border-slate-700/70 bg-gradient-to-br ${project.accent} p-6 ${featured ? 'sm:min-h-[26rem]' : ''}`}>
      <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full border border-white/10" aria-hidden="true" />
      <div className="absolute -bottom-24 -left-10 h-48 w-48 rounded-full border border-white/10" aria-hidden="true" />
      <div className="relative flex h-full min-h-48 flex-col justify-between sm:min-h-64">
        <div className="flex items-start justify-between gap-4">
          <span className="rounded-full border border-white/15 bg-slate-950/30 px-3 py-1 text-[0.65rem] font-bold uppercase tracking-[0.16em] text-slate-300">Project visual</span>
          <Icon className="text-3xl text-white/80 transition duration-300 group-hover/visual:scale-110 group-hover/visual:text-cyan-200" aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-semibold text-slate-300">{project.category}</p>
          <p className="mt-2 text-3xl font-extrabold tracking-tight text-white">{project.name}</p>
          <div className="mt-5 h-px w-2/3 bg-gradient-to-r from-white/60 to-transparent" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

function ProjectLinks({ links, compact = false }) {
  if (!links?.length) return null;

  return (
    <div className={`flex flex-wrap gap-3 ${compact ? 'mt-5' : 'mt-6'}`}>
      {links.map(({ label, url, icon: Icon, external }) => (
        <a
          key={label}
          href={url}
          target={external ? '_blank' : undefined}
          rel={external ? 'noopener noreferrer' : undefined}
          className="focus-ring inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2.5 text-xs font-bold text-slate-200 transition hover:border-cyan-300/60 hover:bg-slate-900 hover:text-cyan-200"
        >
          <Icon aria-hidden="true" />
          {label}
        </a>
      ))}
    </div>
  );
}

function ProjectModal({ project, onClose, triggerRef }) {
  const dialogRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [onClose, triggerRef]);

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/80 p-4 backdrop-blur-sm sm:items-center" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="project-dialog-title" tabIndex="-1" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-700 bg-[#0d1117] p-5 shadow-2xl shadow-black/40 sm:p-8">
        <div className="flex items-start justify-between gap-5">
          <div>
            <p className="section-eyebrow">{project.category}</p>
            <p className="mt-3 text-xs font-semibold text-slate-500">{project.dates} · {project.context}</p>
            <h2 id="project-dialog-title" className="mt-4 text-3xl font-extrabold tracking-tight text-white">{project.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="focus-ring rounded-full border border-slate-700 p-2 text-slate-400 transition hover:border-slate-400 hover:text-white" aria-label={`Close ${project.name} details`}>
            <FaTimes aria-hidden="true" />
          </button>
        </div>
        <p className="mt-6 text-base leading-7 text-slate-300">{project.overview}</p>
        <div className="mt-7 grid gap-7 sm:grid-cols-2">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Key features</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-300">
              {project.features.map(feature => <li key={feature} className="flex gap-2"><span className="text-cyan-300">/</span>{feature}</li>)}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Technologies</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {project.technologies.map(({ name, icon: Icon }) => <span key={name} className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/70 px-3 py-2 text-xs font-semibold text-slate-300"><Icon aria-hidden="true" />{name}</span>)}
            </div>
          </div>
        </div>
        {project.availability && <p className="mt-7 border-l-2 border-amber-300/60 pl-4 text-sm leading-6 text-slate-400">{project.availability}</p>}
        <ProjectLinks links={project.links} />
      </div>
    </div>
  );
}

function ProjectCard({ project, onOpen }) {
  const triggerRef = useRef(null);
  return (
    <article className="surface-card group flex h-full flex-col overflow-hidden p-3 transition duration-300 hover:-translate-y-1 hover:border-slate-500">
      <ProjectVisual project={project} />
      <div className="flex flex-1 flex-col p-4 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-cyan-300">{project.category}</p>
            <p className="mt-1 text-xs font-medium text-slate-500">{project.dates}</p>
            <h3 className="mt-2 text-xl font-bold text-white">{project.name}</h3>
          </div>
          <button ref={triggerRef} type="button" onClick={() => onOpen(project, triggerRef)} className="focus-ring rounded-full border border-slate-700 p-2 text-slate-400 transition hover:border-cyan-300/60 hover:text-cyan-200" aria-label={`View ${project.name} details`}>
            <FaArrowRight aria-hidden="true" />
          </button>
        </div>
        <p className="mt-3 text-sm leading-6 text-slate-400">{project.description}</p>
        {project.links?.length > 0 && <div className="mt-auto pt-5"><ProjectLinks links={project.links} compact /></div>}
      </div>
    </article>
  );
}

export default function Projects() {
  const [selectedProject, setSelectedProject] = useState(null);
  const [triggerRef, setTriggerRef] = useState(null);

  const openProject = (project, ref) => {
    setTriggerRef(ref);
    setSelectedProject(project);
  };

  return (
    <section className="px-4 py-16 sm:px-6 sm:py-24 lg:px-8" id="projects">
      <div className="site-container">
        <div className="mb-8 max-w-2xl sm:mb-12">
          <p className="section-eyebrow">Selected work</p>
          <h2 className="mt-5 text-3xl font-extrabold tracking-tight text-white sm:text-5xl">Projects I&apos;ve built.</h2>
          <p className="mt-5 text-base leading-7 text-slate-400">A selection of practical applications exploring communication, commerce, productivity, and everyday utility.</p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map(project => <ProjectCard key={project.name} project={project} onOpen={openProject} />)}
        </div>
      </div>
      {selectedProject && <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} triggerRef={triggerRef} />}
    </section>
  );
}
